/**
 * Terms gate — before any page, the workspace must have accepted the CURRENT terms version
 * (PAGES.md "Terms gate"). The text shown is the text the server returns with the status, and
 * "I accept" sends exactly that `currentVersion`, so consent is recorded against what was read.
 * Re-shown whenever the version moves. If the status cannot be read the gate stays open, as today
 * (frontend/src/App.tsx): a terms outage must not lock everyone out of their data.
 */
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getClient, newIdempotencyKey } from '@inferrex/api-client';
import { Button, StateBlock, notifyProblem } from '@inferrex/ui';

export const TERMS_STATUS_KEY = ['terms', 'status'] as const;

function useTermsStatus() {
  return useQuery({
    queryKey: TERMS_STATUS_KEY,
    queryFn: () => getClient().terms.getTermsStatus(),
    staleTime: 60_000,
    retry: 1,
  });
}

export function TermsGate({ children }: { children: ReactNode }) {
  const status = useTermsStatus();
  const qc = useQueryClient();
  const accept = useMutation({
    mutationFn: (version: string) => getClient().terms.acceptTerms({ version }, { idempotencyKey: newIdempotencyKey() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: TERMS_STATUS_KEY }),
    onError: (err) => { notifyProblem(err); },
    meta: { handlesError: true },
  });

  if (status.isLoading) {
    return <div className="app-spinner"><StateBlock kind="loading" rows={2} message="Checking the terms…" /></div>;
  }
  const data = status.data;
  if (!data || data.accepted) return <>{children}</>;

  return (
    <div className="app-frame">
      <section className="app-card app-frame__card" aria-labelledby="terms-title">
        <header className="app-terms__head">
          <h1 id="terms-title">Terms of service</h1>
          <p>Please read and accept the terms to keep using Inferrex.</p>
        </header>
        <div className="app-terms" tabIndex={0} aria-label="Terms text">
          {data.content.map((s) => (
            <section key={s.heading}>
              <h2>{s.heading}</h2>
              <p>{s.body}</p>
            </section>
          ))}
        </div>
        <footer className="app-terms__foot">
          <span className="app-terms__version">Version {data.currentVersion}</span>
          <Button onClick={() => accept.mutate(data.currentVersion)} disabled={accept.isPending} aria-busy={accept.isPending || undefined}>
            I accept
          </Button>
        </footer>
      </section>
    </div>
  );
}
